import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Chip,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogContent, DialogContentText, Button,
  CircularProgress,
} from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LocalizedStrings from 'react-localization';

import {
  getSmsTemplatesRequest,
  createSmsTemplatesRequest,
  createSmsTemplatesReset,
  deleteSmsTemplatesRequest,
  deleteSmsTemplatesReset,
  updateSmsTemplatesRequest,
  updateSmsTemplatesReset,
  getSupportedParametersRequest,
} from './actions.es6';
import { getPartnerGuid, getPartner, hasAuthourity } from '../../lib/access.es6';
import { updateCurrentLanguage } from '../../lib/languageUtils.es6';
import { localisedText } from '../../lib/localisation.es6';
import { formatDateLocale } from '../../lib/utils.es6';
import { SmsTemplateCreateUpdate } from '../../components/smsTemplateCreateUpdate/index.es6';

var TH_STYLE = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};

var TD_STYLE = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

function getFlagLabel(t) {
  if (t.system_generated) return 'System Generated';
  if (t.flash) return 'Flash';
  if (t.reply_to) return 'Reply-To';
  return null;
}

const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 20;

export class SmsTemplate extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      labels: new LocalizedStrings(localisedText),
      openDeleteSmsTemplateDialog: false,
      smsTemplateToDelete: null,
      openUpdateSmsTemplateDialog: false,
      smsTemplateToUpdate: null,
      page: DEFAULT_PAGE,
      size: DEFAULT_SIZE,
    };
  }

  componentWillMount() {
    var partnerGuid = getPartnerGuid();
    this.updateLanguage();
    this.props.dispatch(getSupportedParametersRequest(partnerGuid));
    this.props.dispatch(getSmsTemplatesRequest(partnerGuid));
  }

  componentDidUpdate() {
    this.updateLanguage();
  }

  updateLanguage() {
    updateCurrentLanguage(
      this.props.globalData.language,
      'SmsTemplate',
      this.state.labels
    );
  }

  createSmsTemplate(smsTemplatePayload) {
    this.props.dispatch(createSmsTemplatesRequest(smsTemplatePayload));
  }

  resetCreateSmsTemplate() {
    this.props.dispatch(createSmsTemplatesReset());
  }

  updateSmsTemplatePrompt(template) {
    this.setState({ smsTemplateToUpdate: template, openUpdateSmsTemplateDialog: true });
  }

  updateSmsTemplate(payload) {
    this.props.dispatch(updateSmsTemplatesRequest(payload));
  }

  resetUpdateSmsTemplate() {
    this.setState({ openUpdateSmsTemplateDialog: false });
    this.props.dispatch(updateSmsTemplatesReset());
  }

  deleteSmsTemplatePrompt(template) {
    this.setState({ smsTemplateToDelete: template, openDeleteSmsTemplateDialog: true });
  }

  deleteSmsTemplate(guid) {
    this.props.dispatch(deleteSmsTemplatesRequest({ guid: guid, partnerGuid: getPartnerGuid() }));
  }

  cancelDeleteSmsTemplate() {
    this.setState({ openDeleteSmsTemplateDialog: false, smsTemplateToDelete: null });
  }

  resetDeleteSmsTemplate() {
    this.cancelDeleteSmsTemplate();
    this.props.dispatch(deleteSmsTemplatesReset());
  }

  renderSuccessCreatedDialog(smsTemplate) {
    var labels = this.state.labels;
    return (
      <Dialog open keepMounted fullWidth maxWidth="xs">
        <DialogContent sx={{ textAlign: 'center', bgcolor: '#F9F9F9', py: 4 }}>
          <DialogContentText sx={{ color: 'success.main', fontSize: 16, mb: 2 }}>
            {labels.successfullyCreatedSmsTemplate}
          </DialogContentText>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
            {smsTemplate.code}
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Button onClick={this.resetCreateSmsTemplate.bind(this)} variant="contained"
              sx={{ bgcolor: 'success.main', textTransform: 'none' }}>
              {labels.ok}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  renderSuccessUpdatedDialog(smsTemplate) {
    var labels = this.state.labels;
    return (
      <Dialog open keepMounted fullWidth maxWidth="xs">
        <DialogContent sx={{ textAlign: 'center', bgcolor: '#F9F9F9', py: 4 }}>
          <DialogContentText sx={{ color: 'success.main', fontSize: 16, mb: 2 }}>
            {labels.successfullyUpdatedSmsTemplate}
          </DialogContentText>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
            {smsTemplate.code}
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Button onClick={this.resetUpdateSmsTemplate.bind(this)} variant="contained"
              sx={{ bgcolor: 'success.main', textTransform: 'none' }}>
              {labels.ok}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  renderConfirmDeleteDialog() {
    var template = this.state.smsTemplateToDelete;
    var labels = this.state.labels;
    return (
      <Dialog open keepMounted fullWidth maxWidth="xs">
        <DialogContent sx={{ textAlign: 'center', bgcolor: '#F9F9F9', py: 4 }}>
          <DialogContentText sx={{ color: 'error.main', fontSize: 15, mb: 1 }}>
            {labels.deleteTemplateWarningMessage}
          </DialogContentText>
          {template && (
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827', mb: 3 }}>
              {template.code}
            </Typography>
          )}
          <Button onClick={this.deleteSmsTemplate.bind(this, template && template.guid)}
            variant="contained" sx={{ bgcolor: 'error.main', mr: 1, textTransform: 'none' }}>
            {labels.delete}
          </Button>
          <Button onClick={this.cancelDeleteSmsTemplate.bind(this)}
            variant="outlined" sx={{ textTransform: 'none' }}>
            {labels.cancel}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  renderSuccessDeletedDialog() {
    var labels = this.state.labels;
    return (
      <Dialog open keepMounted fullWidth maxWidth="xs">
        <DialogContent sx={{ textAlign: 'center', bgcolor: '#F9F9F9', py: 4 }}>
          <DialogContentText sx={{ color: 'success.main', fontSize: 16, mb: 3 }}>
            {labels.successfullyDeletedSmsTemplate}
          </DialogContentText>
          <Button onClick={this.resetDeleteSmsTemplate.bind(this)} variant="contained"
            sx={{ bgcolor: 'success.main', textTransform: 'none' }}>
            {labels.ok}
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  render() {
    var smsData = this.props.smsTemplates;
    var smses = (smsData.sms_templates ? smsData.sms_templates.items : []) || [];
    var labels = this.state.labels;
    var supportedParameters = (smsData.supported_parameters
      ? smsData.supported_parameters.parameters : []).sort();
    var partner = getPartner();

    return (
      <Box sx={{ p: 3 }}>
        {/* Page header */}
        <Box sx={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between', mb: 3,
        }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 20, color: '#111827' }}>
              {labels.smsTemplates || 'SMS Templates'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.5 }}>
              {partner ? partner.name + ' — ' : ''}notification and transactional SMS templates
            </Typography>
          </Box>
          <SmsTemplateCreateUpdate
            type="create"
            supportedParameters={supportedParameters}
            createSmsTemplate={this.createSmsTemplate.bind(this)}
            globalData={this.props.globalData}
          />
        </Box>

        {/* Loading spinner dialog */}
        {smsData.loader && (
          <Dialog open>
            <DialogContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={48} />
            </DialogContent>
          </Dialog>
        )}

        {/* State dialogs */}
        {smsData.successfully_deleted_sms_template && this.renderSuccessDeletedDialog()}
        {this.state.openDeleteSmsTemplateDialog && this.state.smsTemplateToDelete &&
          this.renderConfirmDeleteDialog()}
        {smsData.successfully_created_sms_template &&
          this.renderSuccessCreatedDialog(smsData.successfully_created_sms_template)}
        {smsData.successfully_updated_sms_template &&
          this.renderSuccessUpdatedDialog(smsData.successfully_updated_sms_template)}

        {/* Inline edit form */}
        {this.state.openUpdateSmsTemplateDialog && this.state.smsTemplateToUpdate && (
          <Box sx={{ mb: 3 }}>
            <SmsTemplateCreateUpdate
              type="update"
              supportedParameters={supportedParameters}
              updateSmsTemplate={this.updateSmsTemplate.bind(this)}
              smsTemplateToUpdate={this.state.smsTemplateToUpdate}
              closeDialogUpdateSmsTemplate={this.resetUpdateSmsTemplate.bind(this)}
              globalData={this.props.globalData}
            />
          </Box>
        )}

        {/* Table */}
        <TableContainer sx={{
          border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden',
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={TH_STYLE}>Template</TableCell>
                <TableCell sx={TH_STYLE}>Code</TableCell>
                <TableCell sx={TH_STYLE}>Language</TableCell>
                <TableCell sx={TH_STYLE}>Date</TableCell>
                <TableCell sx={TH_STYLE}>Flag</TableCell>
                <TableCell sx={Object.assign({}, TH_STYLE, { textAlign: 'right' })}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {smsData.sms_templates && smses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <SmsOutlinedIcon sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
                      <Typography sx={{ fontSize: 14, color: '#9CA3AF' }}>
                        No SMS templates found
                        {partner ? ' for ' + partner.name : ''}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {smses.map(function(t) {
                var flag = getFlagLabel(t);
                var label = (t.code || '').replace(/_/g, ' ');
                var lang = t.language ? t.language.toUpperCase() : '';
                return (
                  <TableRow
                    key={t.guid || t.code}
                    sx={{
                      '&:hover': { bgcolor: '#FAFAFA' },
                      '&:last-child td': { border: 0 },
                    }}
                  >
                    {/* Name chip */}
                    <TableCell sx={TD_STYLE}>
                      <Chip
                        label={label + (lang ? ' (' + lang + ')' : '')}
                        size="small"
                        sx={{
                          height: 22, fontSize: 11, fontWeight: 600,
                          bgcolor: '#FFF7ED', color: '#C2410C',
                          border: '1px solid #FED7AA', maxWidth: 260,
                        }}
                      />
                    </TableCell>

                    {/* Code */}
                    <TableCell sx={TD_STYLE}>
                      <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#6B7280' }}>
                        {t.code || '—'}
                      </Typography>
                    </TableCell>

                    {/* Language */}
                    <TableCell sx={TD_STYLE}>
                      {lang ? (
                        <Chip label={lang} size="small" sx={{
                          height: 20, fontSize: 10, fontWeight: 700,
                          bgcolor: '#EEF2FF', color: '#3730A3',
                          border: '1px solid #C7D2FE',
                        }} />
                      ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </TableCell>

                    {/* Date */}
                    <TableCell sx={TD_STYLE}>
                      <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                        {t.created_at ? formatDateLocale(t.created_at) : '—'}
                      </Typography>
                    </TableCell>

                    {/* Flag */}
                    <TableCell sx={TD_STYLE}>
                      {flag ? (
                        <Chip label={flag} size="small" sx={{
                          height: 20, fontSize: 10, fontWeight: 500,
                          bgcolor: '#F0F9FF', color: '#0369A1',
                          border: '1px solid #BAE6FD',
                        }} />
                      ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={Object.assign({}, TD_STYLE, { textAlign: 'right' })}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {hasAuthourity('UPDATE_SMS_TEMPLATE') && (
                          <Tooltip title="Edit">
                            <IconButton size="small"
                              onClick={function() {
                                this.updateSmsTemplatePrompt(t);
                              }.bind(this)}
                              sx={{ color: '#0D47A1', p: 0.75 }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasAuthourity('DELETE_SMS_TEMPLATE') && (
                          <Tooltip title="Delete">
                            <IconButton size="small"
                              onClick={function() {
                                this.deleteSmsTemplatePrompt(t);
                              }.bind(this)}
                              sx={{ color: '#B91C1C', p: 0.75 }}
                            >
                              <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }.bind(this))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }
}

export default connect(function(state) {
  return {
    smsTemplates: state.smsTemplates,
    globalData: state.global,
  };
})(SmsTemplate);
